// Банк и бухгалтерия — зеркало портала buh. ТОЛЬКО ЧТЕНИЕ.
//
// ЧТО ЭТОТ ЭКРАН ОТВЕЧАЕТ. Первая вкладка — не «выписка», а находка, ради которой раздел
// и сделан: 1 381 операция (на 28.07.2026) прошла по расчётному счёту и НЕ ПРИВЯЗАНА ни
// к сделке, ни к счёту. Это 503,8 млн ₽, из них 502,2 млн прихода. Проверено запросом
// на живых данных 28.07.2026, и это же число ядро считает заново на каждый запрос.
//
// Остальные три вкладки — то, без чего находку не проверить:
//   • «Выписка» — сама операция, её назначение и разбор портала;
//   • «Сальдо» — 75 счетов бухучёта с вердиктом аудита (8 ошибок, 13 предупреждений);
//   • «Аудит и заливки» — кто и когда сказал «разобрано», тренд счётчиков 1С и
//     происхождение самой выписки (одна заливка, 12 798 строк из 12 800 на 28.07.2026).
//
// ГЛАВНАЯ ЛОЖЬ, КОТОРУЮ ЭКРАН ОБЯЗАН НЕ СКАЗАТЬ. Портал помечает 1 580 операций (28.07.2026)
// как «оплата по сделке N», вытащив N из текста назначения. У 77 из них N равно СУММЕ платежа
// («…Сумма 109300-00» → «сделка 109300»), и 53 таких числа существуют в Bitrix как живые
// чужие сделки — ядро вернёт даже их названия. Поэтому гипотеза здесь нигде не показана
// ссылкой: она подписана «гипотеза портала», а совпавшая с суммой — отдельным красным
// предупреждением. Ссылку в CRM ставит только человек, подтвердивший платёж в 1С.
//
// ПРО ДЕНЬГИ. Это расчётный счёт компании целиком. Ядро помечает каждый ответ
// restricted='finance'; экран эту пометку ПОКАЗЫВАЕТ — человек должен видеть, что смотрит
// на выписку, а не на обычный отчёт. Штрафов ГИБДД и разбора входящих документов здесь нет
// вовсе: ядро эти таблицы не читает (pbh_api.ЗАПРЕЩЕНО), и экран говорит, что они есть
// в зеркале и почему закрыты.
import { useMemo, useState } from 'react'

import {
  useАудит, useВыписка, useЗаливки, useНеразнесённое, usePBHRefs, useСальдо,
} from '../api/pbh'
import type {
  ОперацияБанка, СнимокАудита, СчётСальдо,
} from '../api/pbh'
import { EmptySource } from '../components/EmptySource'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, plural, pluralN } from '../lib/format'
import {
  ВАЖНЫЕ_ОГОВОРКИ, ИМЯ_СУЩНОСТИ, ИМЯ_СЧЁТЧИКА, РАЗМЕР_СТРАНИЦЫ, видВердикта, видДействия,
  видНаправления, видРазбора, видСчёта, довериеКСделке, кучиНеразнесённого, месяцСловами,
  свежесть, трендНеразнесённого,
} from '../lib/pbh'

type Вкладка = 'unlinked' | 'bank' | 'balances' | 'audit'

export default function Bank() {
  const [вкладка, setВкладка] = useState<Вкладка>('unlinked')
  const refs = usePBHRefs()

  const св = свежесть(refs.data?.freshness)
  const счёт = refs.data?.counts

  return (
    <>
      <div className="page-head">
        <h1>Банк и бухгалтерия</h1>
        <span className="spacer" />
        <span className="hint">
          {св
            ? `обновлено ${св.age_min < 1 ? 'только что' : `${Math.round(св.age_min)} мин назад`}`
              + ` (позже всех — ${ИМЯ_СУЩНОСТИ[св.entity] ?? св.entity})`
            : 'свежесть неизвестна'}
        </span>
      </div>

      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Здесь расчётный счёт компании:</b> суммы, контрагенты и назначения платежей.
        Разносит платежи бухгалтер на портале buh, в CRM только обзор и поиск.
        {refs.data?.restricted_why && ` Ядро помечает эти данные закрытыми: ${refs.data.restricted_why}.`}
      </div>

      {/* Переключатель вкладок — обычный flex, а НЕ .filters: на телефоне правило
          «.filters > * { width: 100% }» поставило бы четыре кнопки в столбец и съело
          весь первый экран до единственного, ради чего его открыли. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {([
          ['unlinked', `Не привязано${счёт ? ` · ${счёт.unlinked}` : ''}`],
          ['bank', `Выписка${счёт ? ` · ${счёт.bank_rows}` : ''}`],
          ['balances', `Сальдо${счёт
            ? ` · ${pluralN(счёт.accounts_err, 'ошибка', 'ошибки', 'ошибок')}` : ''}`],
          ['audit', 'Аудит и заливки'],
        ] as [Вкладка, string][]).map(([код, подпись]) => (
          <button key={код} onClick={() => setВкладка(код)}
                  aria-pressed={вкладка === код}
                  style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}>
            {подпись}
          </button>
        ))}
      </div>

      <QueryState isLoading={refs.isLoading} error={refs.error}>
        {вкладка === 'unlinked' && <Неразнесённое />}
        {вкладка === 'bank' && <Выписка />}
        {вкладка === 'balances' && <Сальдо />}
        {вкладка === 'audit' && <АудитИЗаливки />}
        <Закрытое закрыто={refs.data?.closed} />
        <ПравилаРазнесения строк={счёт?.rules} />
        <Оговорки заметки={refs.data?.data_notes} />
      </QueryState>
    </>
  )
}

/** Оговорки из ядра. Показываются на всех вкладках: без них числа читаются неверно. */
function Оговорки({ заметки }: { заметки?: Record<string, string> }) {
  if (!заметки) return null
  const важные = ВАЖНЫЕ_ОГОВОРКИ.filter((к) => заметки[к])
  if (!важные.length) return null
  return (
    <div className="card hint" style={{ marginTop: 12 }}>
      <b>Что нужно знать об этих данных</b>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        {важные.map((к) => <li key={к}>{заметки[к]}</li>)}
      </ul>
    </div>
  )
}

/**
 * Что в зеркале есть, но раздел не показывает. Молчать об этом нельзя: человек, который
 * ищет штраф и не находит, решит, что штрафов нет, — а они есть, просто закрыты.
 */
function Закрытое({ закрыто }: { закрыто?: { entity: string; why: string; rows: number | null }[] }) {
  if (!закрыто?.length) return null
  return (
    <div className="card hint" style={{ marginTop: 12 }}>
      <b>Есть в зеркале, но раздел не показывает</b>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        {закрыто.map((з) => (
          <li key={з.entity}>
            {з.entity === 'pbh:fines' ? 'Штрафы ГИБДД' : 'Разбор входящих документов'}
            {з.rows !== null ? ` (${з.rows} в зеркале)` : ''}: {з.why}. Ядро эту таблицу
            не читает вовсе — открыть её может только владелец, отдельным правом.
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Правила автоматического разнесения платежей. Ядро отдаёт их число в `counts.rules` с
 * самого переноса раздела, и число это НОЛЬ — а на экране его не было вовсе.
 *
 * Молчать нельзя ровно по той же причине, по какой существует «Закрытое» выше: человек,
 * который ищет, почему платежи не разносятся сами, не найдёт ни правил, ни объяснения — и
 * решит, что правила есть, просто CRM их не показывает. Их нет.
 */
function ПравилаРазнесения({ строк }: { строк?: number }) {
  if (строк === undefined || строк > 0) return null
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <EmptySource
        что="Правил автоматического разнесения платежей"
        строк={строк}
        источник="портал buh"
        таблица="pbh_rules"
        почему="ни одного правила там не завели: платежи разносит бухгалтер руками"
      />
    </div>
  )
}

function Страницы({ всего, start, setStart }: {
  всего: number; start: number; setStart: (v: number) => void
}) {
  if (всего <= РАЗМЕР_СТРАНИЦЫ) return null
  return (
    <div className="pager">
      <button disabled={start === 0} onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
        назад
      </button>
      <span className="muted">
        {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, всего)} из {всего}
      </span>
      <button disabled={start + РАЗМЕР_СТРАНИЦЫ >= всего}
              onClick={() => setStart(start + РАЗМЕР_СТРАНИЦЫ)}>
        дальше
      </button>
    </div>
  )
}

// ═══════════════════════ вкладка «Не привязано» — находка раздела ═══════════════════════

function Неразнесённое() {
  const св = useНеразнесённое()
  const t = св.data?.totals

  return (
    <QueryState isLoading={св.isLoading} error={св.error}>
      {t && <ПлиткиНаходки t={t} />}
      {св.data && <ДвеКучи данные={св.data} />}
      {св.data && <ТрендПоМесяцам месяцы={св.data.by_month} />}
      {св.data && <ПричиныИКонтрагенты данные={св.data} />}
      <СписокНеразнесённого />
    </QueryState>
  )
}

function ПлиткиНаходки({ t }: { t: NonNullable<ReturnType<typeof useНеразнесённое>['data']>['totals'] }) {
  return (
    <div className="tiles" style={{ marginBottom: 12 }}>
      <div className="tile hot">
        <div className="t-label">операций ни к чему не привязано</div>
        <div className="t-sum">{t.n ?? 0}</div>
        <div className="t-label">
          {fmtDate(t.first_day)} — {fmtDate(t.last_day)}
        </div>
      </div>
      <div className="tile hot">
        <div className="t-label">пришло и не разнесено</div>
        <div className="t-sum">{fmtMoney(t.in_rub ?? 0)}</div>
        <div className="t-label">{t.in_n ?? 0} приходов</div>
      </div>
      <div className="tile">
        <div className="t-label">ушло и не разнесено</div>
        <div className="t-sum">{fmtMoney(t.out_rub ?? 0)}</div>
        <div className="t-label">{t.out_n ?? 0} расходов</div>
      </div>
      <div className="tile">
        <div className="t-label">контрагентов</div>
        <div className="t-sum">{t.parties ?? 0}</div>
        <div className="t-label">по неразнесённым операциям</div>
      </div>
    </div>
  )
}

/**
 * Две кучи внутри находки. Отдельным блоком, а не сноской: у 1 283 операций (на 28.07.2026)
 * номер счёта в назначении ЕСТЬ (искать в 1С), а у 98 нет ничего. Это две разные работы,
 * и одно число «1 381» скрывает, что делать.
 */
function ДвеКучи({ данные }: { данные: NonNullable<ReturnType<typeof useНеразнесённое>['data']> }) {
  const к = кучиНеразнесённого(данные.totals)
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <b>Это две разные кучи, а не одна</b>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        <li>
          <b>{pluralN(к.сЗацепкой, 'операция', 'операции', 'операций')}</b>
          {' '}на {fmtMoney(данные.totals.with_refs_rub ?? 0)} —
          номер счёта в назначении есть, но в ядре нет реестра, к которому его привязать:
          у смарт-счетов Bitrix номера повторяются по годам, а номера 1С в ядре только
          с апреля 2026. Разбирать в 1С.
        </li>
        <li>
          <b>{pluralN(к.безЗацепки, 'операция', 'операции', 'операций')}</b>
          {' '}на {fmtMoney(данные.totals.no_refs_rub ?? 0)} —
          зацепок нет вовсе: новый плательщик либо назначение не опознано.
        </li>
      </ul>
      {!к.сходится && (
        <div className="error" style={{ fontSize: 12, marginTop: 6 }}>
          Кучи не складываются в общее число — ядро и экран считают по-разному, цифрам
          верить нельзя.
        </div>
      )}
      {данные.data_notes?.two_piles && (
        <div className="hint" style={{ marginTop: 6 }}>
          {данные.data_notes.two_piles}
        </div>
      )}
    </div>
  )
}

/**
 * Тренд по месяцам. Без него «1 381 непривязанная операция» читается как «бухгалтерия
 * не работает», а доля падает с 19,2 % в ноябре 2025 до единиц процентов к лету.
 * Сравниваем ДОЛЮ, а не количество: в июле операций 206, в апреле 1 832 (на 28.07.2026).
 */
function ТрендПоМесяцам({ месяцы }: { месяцы: NonNullable<ReturnType<typeof useНеразнесённое>['data']>['by_month'] }) {
  if (!месяцы.length) return null
  const т = трендНеразнесённого(месяцы)
  const макс = Math.max(...месяцы.map((м) => м.share_pct ?? 0), 1)
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <b>Становится ли лучше</b>
      {т.лучше !== null && (
        <div className="hint" style={{ marginTop: 4 }}>
          {т.лучше
            ? `Да: доля неразнесённого упала с ${т.первый} % до ${т.последнийПолный} %.`
            : `Нет: доля выросла с ${т.первый} % до ${т.последнийПолный} %.`}
          {' '}Последний месяц в зеркале неполный — выписка обрывается 13.07.2026, поэтому
          в сравнении он не участвует.
        </div>
      )}
      <div className="table-wrap cards" style={{ marginTop: 8 }}>
        <table>
          <thead>
            <tr>
              <th>Месяц</th>
              <th className="num">Не привязано</th>
              <th className="num">Всего операций</th>
              <th className="num">Доля</th>
              <th className="num">Сумма</th>
            </tr>
          </thead>
          <tbody>
            {месяцы.map((м) => (
              <tr key={м.month}>
                <td data-label="Месяц" className="nowrap">{месяцСловами(м.month)}</td>
                <td className="num" data-label="Не привязано">{м.n}</td>
                <td className="num muted" data-label="Всего операций">{м.month_total}</td>
                <td className="num" data-label="Доля">
                  {м.share_pct === null ? '—' : `${м.share_pct} %`}
                  {/* Полоска, а не график: на 375px график нечитаем, а полоска в ячейке
                      остаётся полоской и в карточке. */}
                  {м.share_pct !== null && (
                    <div aria-hidden="true" style={{
                      height: 4, marginTop: 3, borderRadius: 2,
                      width: `${Math.round((м.share_pct / макс) * 100)}%`,
                      background: 'currentColor', opacity: 0.35, marginLeft: 'auto',
                    }} />
                  )}
                </td>
                <td className="num" data-label="Сумма">{fmtMoney(м.rub ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ПричиныИКонтрагенты({ данные }: {
  данные: NonNullable<ReturnType<typeof useНеразнесённое>['data']>
}) {
  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <b>Почему портал не смог привязать</b>
        <div className="table-wrap cards" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Причина, как её назвал портал</th>
                <th className="num">Операций</th>
                <th className="num">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {данные.by_reason.map((п) => (
                <tr key={п.why}>
                  <td data-label="Причина">{п.why}</td>
                  <td className="num" data-label="Операций">{п.n}</td>
                  <td className="num" data-label="Сумма">{fmtMoney(п.rub ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <b>Кому больше всего денег «висит» неразнесёнными</b>
        <div className="hint" style={{ marginTop: 4 }}>
          Двадцать контрагентов с наибольшей суммой. Компанию в CRM по ИНН здесь не
          подставить: в ядре ИНН у компаний не хранится — искать по названию.
        </div>
        <div className="table-wrap cards" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Контрагент</th>
                <th className="num">Операций</th>
                <th className="num">Сумма</th>
                <th>Период</th>
              </tr>
            </thead>
            <tbody>
              {данные.top_parties.map((к) => (
                <tr key={`${к.cp_inn}-${к.cp_name}`}>
                  <td data-label="Контрагент">
                    <div>{к.cp_name}</div>
                    <div className="muted mono">
                      {к.cp_inn ? `ИНН ${к.cp_inn}` : 'ИНН не указан'}
                    </div>
                  </td>
                  <td className="num" data-label="Операций">{к.n}</td>
                  <td className="num" data-label="Сумма">{fmtMoney(к.rub ?? 0)}</td>
                  <td data-label="Период" className="nowrap">
                    {fmtDate(к.first_day)}
                    <div className="hint">{fmtDate(к.last_day)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

/** Сами операции. Тот же компонент таблицы, что на вкладке «Выписка» — одни правила показа. */
function СписокНеразнесённого() {
  const [start, setStart] = useState(0)
  const список = useВыписка({ unlinked: true, start, limit: РАЗМЕР_СТРАНИЦЫ })
  return (
    <div className="card">
      <b>Сами операции</b>
      <div className="hint" style={{ marginTop: 4 }}>
        Всё то же, что в «Выписке», но только непривязанное. Порядок — от свежих к старым.
      </div>
      <QueryState isLoading={список.isLoading} error={список.error}>
        <ТаблицаВыписки строки={список.data?.rows ?? []}
                        пусто="Непривязанных операций нет — всё разнесено" />
        <Страницы всего={список.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </div>
  )
}

// ═══════════════════════════════ вкладка «Выписка» ═══════════════════════════════

function Выписка() {
  const refs = usePBHRefs()
  const [месяц, setМесяц] = useState('')
  const [статус, setСтатус] = useState('')
  const [направление, setНаправление] = useState('')
  const [поиск, setПоиск] = useState('')
  const [номер, setНомер] = useState('')
  const [минимум, setМинимум] = useState('')
  const [start, setStart] = useState(0)

  const фильтры = useMemo(() => ({
    month: месяц || undefined,
    status: статус || undefined,
    dir: направление || undefined,
    q: поиск.trim() || undefined,
    ref: номер.trim() || undefined,
    min: минимум.trim() || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [месяц, статус, направление, поиск, номер, минимум, start])

  const список = useВыписка(фильтры)
  const итоги = список.data?.totals
  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        {/* Шесть полей в столбец на телефоне — это весь экран до первой строки выписки,
            поэтому блок сворачивается; что применено, видно в его заголовке. */}
        <FilterBar id="bank-statement">
          <div className="f">
            <label htmlFor="bkm">Месяц</label>
            <select id="bkm" value={месяц} onChange={(e) => сСброса(setМесяц)(e.target.value)}>
              <option value="">весь период</option>
              {Object.entries(refs.data?.months ?? {}).map(([м, n]) => (
                <option key={м} value={м}>{месяцСловами(м)} ({n})</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="bks">Разбор</label>
            <select id="bks" value={статус} onChange={(e) => сСброса(setСтатус)(e.target.value)}>
              <option value="">любой</option>
              {Object.entries(refs.data?.statuses ?? {}).filter(([к]) => к && к !== '—')
                .map(([к, n]) => (
                  <option key={к} value={к}>{видРазбора(к).ярлык} ({n})</option>
                ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="bkd">Направление</label>
            <select id="bkd" value={направление}
                    onChange={(e) => сСброса(setНаправление)(e.target.value)}>
              <option value="">приход и расход</option>
              <option value="in">только приход</option>
              <option value="out">только расход</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="bkq">Контрагент или назначение</label>
            <input id="bkq" value={поиск} placeholder="МОРВЕННА, ИНН, номер документа"
                   onChange={(e) => сСброса(setПоиск)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="bkr">Номер счёта или сделки</label>
            <input id="bkr" value={номер} placeholder="6564"
                   onChange={(e) => сСброса(setНомер)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="bkmin">Сумма от, ₽</label>
            <input id="bkmin" value={минимум} placeholder="100000"
                   onChange={(e) => сСброса(setМинимум)(e.target.value)} />
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          Поиск по номеру ищет ЦЕЛЫЙ номер в списке из назначения платежа: «26» не найдёт
          «2604». Номер сделки ищется и в гипотезе портала, и в номерах из назначения —
          обе половины гипотезы, и какая верна, решает бухгалтер.
        </div>
      </div>

      {итоги && <ПлиткиВыписки итоги={итоги} />}

      <QueryState isLoading={список.isLoading} error={список.error}>
        <ТаблицаВыписки строки={список.data?.rows ?? []}
                        пусто="Под эти условия операций нет" />
        <Страницы всего={список.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ПлиткиВыписки({ итоги }: { итоги: NonNullable<ReturnType<typeof useВыписка>['data']>['totals'] }) {
  return (
    <div className="tiles" style={{ marginBottom: 12 }}>
      <div className="tile">
        <div className="t-label">операций в выборке</div>
        <div className="t-sum">{итоги.n ?? 0}</div>
        <div className="t-label">контрагентов: {итоги.parties ?? 0}</div>
      </div>
      <div className="tile">
        <div className="t-label">приход</div>
        <div className="t-sum">{fmtMoney(итоги.in_rub ?? 0)}</div>
        <div className="t-label">
          {pluralN(итоги.in_n ?? 0, 'операция', 'операции', 'операций')}
        </div>
      </div>
      <div className="tile">
        <div className="t-label">расход</div>
        <div className="t-sum">{fmtMoney(итоги.out_rub ?? 0)}</div>
        <div className="t-label">
          {pluralN(итоги.out_n ?? 0, 'операция', 'операции', 'операций')}
        </div>
      </div>
      <div className={(итоги.unlinked_n ?? 0) > 0 ? 'tile hot' : 'tile'}>
        <div className="t-label">из них не привязано</div>
        <div className="t-sum">{итоги.unlinked_n ?? 0}</div>
        <div className="t-label">{fmtMoney(итоги.unlinked_rub ?? 0)}</div>
      </div>
      {(итоги.guess_is_amount_n ?? 0) > 0 && (
        <div className="tile hot">
          <div className="t-label">номер сделки равен сумме платежа</div>
          <div className="t-sum">{итоги.guess_is_amount_n}</div>
          <div className="t-label">
            из {итоги.deal_guess_n ?? 0} гипотез портала — разбирать руками
          </div>
        </div>
      )}
    </div>
  )
}

function ТаблицаВыписки({ строки, пусто }: { строки: ОперацияБанка[]; пусто: string }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">{пусто}</div>
        <div className="empty-hint">
          Выписка в зеркале за 31.10.2025 — 13.07.2026 и заливается в портал вручную одним
          файлом: пусто может значить «файл за этот период не залили».
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Дата</th>
            <th>Контрагент</th>
            <th className="num">Сумма</th>
            <th>Назначение</th>
            <th>Разбор портала</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => <СтрокаВыписки key={r.src_id} r={r} />)}
        </tbody>
      </table>
    </div>
  )
}

function СтрокаВыписки({ r }: { r: ОперацияБанка }) {
  const разбор = видРазбора(r.status)
  const напр = видНаправления(r.dir)
  const дов = довериеКСделке(r)
  return (
    <tr>
      <td data-label="Дата">
        <div className="nowrap">{fmtDate(r.op_date) || r.op_date_raw || '—'}</div>
        <div className="muted mono">
          {r.docnum ? `док. ${r.docnum}` : 'без номера'}
        </div>
      </td>
      <td data-label="Контрагент">
        <div>{r.cp_name || 'без названия'}</div>
        <div className="muted mono">
          {r.cp_inn ? `ИНН ${r.cp_inn}` : 'ИНН не указан'}
        </div>
      </td>
      <td className="num" data-label="Сумма">
        <span className="nowrap">{напр.знак}{fmtMoney(r.amount ?? 0)}</span>
        <div>
          <span className={напр.класс} title={`${напр.ярлык} по расчётному счёту`}>
            {напр.ярлык}
          </span>
        </div>
      </td>
      <td data-label="Назначение">
        {/* Назначение платежа не обрезаем: именно в нём номер счёта, и именно по нему
            бухгалтер узнаёт платёж. Обрезанный текст здесь бесполезен. */}
        <div style={{ fontSize: 12 }}>{r.purpose || 'назначение не указано'}</div>
        {r.refs_list.length > 0 && (
          <div className="muted mono" style={{ marginTop: 2 }}>
            номера из назначения: {r.refs_list.join(', ')}
          </div>
        )}
      </td>
      <td data-label="Разбор портала">
        <span className={разбор.класс} title={разбор.смысл}>{разбор.ярлык}</span>
        {r.cat && <div style={{ fontSize: 12, marginTop: 2 }}>{r.cat}</div>}
        {r.why && <div className="hint">{r.why}</div>}
        {/* Гипотеза о сделке — НЕ ссылка. См. шапку файла: у 77 операций этот номер равен
            сумме платежа, и 53 из них ведут на живую чужую сделку. */}
        {дов.есть && (
          <div style={{ fontSize: 12, marginTop: 2 }}>
            {дов.ложная ? (
              <span className="error" title={дов.смысл}>
                сделка {r.deal_guess}? {дов.ярлык} — проверять руками
              </span>
            ) : (
              <span className="muted" title={дов.смысл}>
                {дов.ярлык}: сделка {r.deal_guess}
                {r.deal_guess_title ? ` — ${r.deal_guess_title}` : ''}
              </span>
            )}
          </div>
        )}
        {r.decided_by && (
          <div className="hint">
            разнёс {r.decided_by}{r.decided_at ? `, ${fmtDate(r.decided_at)}` : ''}
          </div>
        )}
      </td>
    </tr>
  )
}

// ═══════════════════════════════ вкладка «Сальдо» ═══════════════════════════════

function Сальдо() {
  const [толькоПлохие, setТолькоПлохие] = useState(true)
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)
  const запрос = useСальдо({
    bad: толькоПлохие || undefined,
    q: поиск.trim() || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  })
  const итоги = запрос.data?.totals

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="bank-ledger">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={толькоПлохие}
                   onChange={(e) => { setStart(0); setТолькоПлохие(e.target.checked) }} />
            только с замечаниями
          </label>
          <div className="f">
            <label htmlFor="blq">Счёт или примечание</label>
            <input id="blq" value={поиск} placeholder="10.03, топливо"
                   onChange={(e) => { setStart(0); setПоиск(e.target.value) }} />
          </div>
        </FilterBar>
        {итоги?.snapshot_at && (
          <div className="hint" style={{ marginTop: 8 }}>
            Это <b>снимок на {fmtDate(итоги.snapshot_at, true)}</b>, а не сальдо на сегодня:
            аудит заливает таблицу целиком в момент прогона, между прогонами она не меняется.
          </div>
        )}
      </div>

      {итоги && (
        <div className="tiles" style={{ marginBottom: 12 }}>
          <div className={(итоги.err_n ?? 0) > 0 ? 'tile hot' : 'tile'}>
            <div className="t-label">ошибки</div>
            <div className="t-sum">{итоги.err_n ?? 0}</div>
            <div className="t-label">активный счёт с кредитовым сальдо и подобное</div>
          </div>
          <div className={(итоги.warn_n ?? 0) > 0 ? 'tile hot' : 'tile'}>
            <div className="t-label">предупреждения</div>
            <div className="t-sum">{итоги.warn_n ?? 0}</div>
            <div className="t-label">свёрнуто верно, но есть «красные» субконто</div>
          </div>
          <div className="tile">
            <div className="t-label">в порядке</div>
            <div className="t-sum">{итоги.ok_n ?? 0}</div>
            <div className="t-label">всего счетов в снимке: {итоги.n ?? 0}</div>
          </div>
        </div>
      )}

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <ТаблицаСальдо строки={запрос.data?.rows ?? []} всего={итоги?.n ?? undefined} />
        <Страницы всего={запрос.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>

      {запрос.data?.data_notes?.sum_is_meaningless && (
        <div className="hint" style={{ marginTop: 8 }}>
          {запрос.data.data_notes.sum_is_meaningless}
        </div>
      )}
    </>
  )
}

function ТаблицаСальдо({ строки, всего }: { строки: СчётСальдо[]; всего?: number }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Счетов с замечаниями нет</div>
        <div className="empty-hint">
          Снимите галочку «только с замечаниями», чтобы увидеть{всего ? ` все ${всего}` : ''}{' '}{plural(всего ?? 0, 'счёт', 'счёта', 'счетов')} снимка.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Счёт</th>
            <th>Тип</th>
            <th className="num">Сальдо</th>
            <th>Вердикт аудита</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((с) => {
            const в = видВердикта(с.status)
            return (
              <tr key={с.code}>
                <td data-label="Счёт">
                  <div className="mono nowrap">{с.code}</div>
                  <div className="hint">{с.name || '—'}</div>
                </td>
                <td data-label="Тип" className="nowrap">{видСчёта(с.acct_type)}</td>
                <td className="num" data-label="Сальдо">
                  {/* Складывать сальдо разных счетов нельзя, поэтому итога по деньгам
                      в таблице нет — только значение в строке. */}
                  {fmtMoney(с.saldo ?? 0)}
                  {(с.debit || с.credit) ? (
                    <div className="hint">
                      дебет {fmtMoney(с.debit ?? 0)} · кредит {fmtMoney(с.credit ?? 0)}
                    </div>
                  ) : null}
                </td>
                <td data-label="Вердикт аудита">
                  <span className={в.класс}>{в.ярлык}</span>
                  {с.note && <div style={{ fontSize: 12, marginTop: 2 }}>{с.note}</div>}
                  {с.by_user && (
                    <div className="hint">прогон: {с.by_user}</div>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════ вкладка «Аудит и заливки» ═══════════════════════

function АудитИЗаливки() {
  const аудит = useАудит()
  const заливки = useЗаливки()

  return (
    <>
      <QueryState isLoading={аудит.isLoading} error={аудит.error}>
        <ЖурналАудита данные={аудит.data} />
        <ТрендАудита снимки={аудит.data?.snapshots ?? []} />
      </QueryState>
      <QueryState isLoading={заливки.isLoading} error={заливки.error}>
        <ЖурналЗаливок данные={заливки.data} />
      </QueryState>
    </>
  )
}

function ЖурналАудита({ данные }: { данные?: NonNullable<ReturnType<typeof useАудит>['data']> }) {
  if (!данные) return null
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <b>Кто и когда сказал «разобрано»</b>
      <div className="hint" style={{ marginTop: 4 }}>
        {данные.data_notes?.checks_are_live}
      </div>
      {!данные.log.length ? (
        <div className="muted" style={{ marginTop: 8 }}>
          Журнал пуст: бухгалтерия ещё не отмечала проверки разобранными.
        </div>
      ) : (
        <div className="table-wrap cards" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Когда</th>
                <th>Проверка</th>
                <th className="num">Счётчик был</th>
                <th>Что сделали</th>
                <th>Кто</th>
              </tr>
            </thead>
            <tbody>
              {данные.log.map((з) => {
                const д = видДействия(з.action)
                return (
                  <tr key={з.src_id ?? з.chk_key}>
                    <td data-label="Когда" className="nowrap">{fmtDate(з.at, true)}</td>
                    <td data-label="Проверка">
                      <div>{з.title || з.chk_key || '—'}</div>
                      <div className="muted mono">{з.chk_key}</div>
                    </td>
                    <td className="num" data-label="Счётчик был">{з.val ?? '—'}</td>
                    <td data-label="Что сделали"><span className={д.класс}>{д.ярлык}</span></td>
                    <td data-label="Кто">{з.by_user || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {!данные.marks.length && данные.data_notes?.marks_disappear && (
        <div className="hint" style={{ marginTop: 8 }}>
          Текущих отметок «разобрано» нет. {данные.data_notes.marks_disappear}
        </div>
      )}
    </div>
  )
}

/**
 * Тренд счётчиков 1С между прогонами аудита. Это единственное, чего в живой 1С уже не
 * достать: портал хранит последние 60 снимков и остальное удаляет.
 *
 * Показываем не «сколько сейчас», а «на сколько изменилось»: голое число непроведённых
 * без движения не говорит, стало лучше или хуже.
 */
function ТрендАудита({ снимки }: { снимки: СнимокАудита[] }) {
  if (!снимки.length) return null
  const свежий = снимки[0]
  const ключи = Object.keys(свежий.data)
    .filter((к) => (свежий.data[к] ?? 0) !== 0 || (свежий.delta?.[к] ?? 0) !== 0)
    .sort((a, b) => (свежий.data[b] ?? 0) - (свежий.data[a] ?? 0))
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <b>Счётчики аудита 1С на {fmtDate(свежий.ts, true)}</b>
      <div className="hint" style={{ marginTop: 4 }}>
        Изменение — к предыдущему прогону{снимки.length > 1 ? ` (${fmtDate(снимки[1].ts, true)})` : ''}.
        {снимки[0].delta === null && ' Предыдущего прогона в зеркале нет — сравнивать не с чем.'}
      </div>
      <div className="table-wrap cards" style={{ marginTop: 8 }}>
        <table>
          <thead>
            <tr>
              <th>Проверка</th>
              <th className="num">Сейчас</th>
              <th className="num">Изменение</th>
            </tr>
          </thead>
          <tbody>
            {ключи.map((к) => {
              const д = свежий.delta?.[к]
              return (
                <tr key={к}>
                  <td data-label="Проверка">
                    {ИМЯ_СЧЁТЧИКА[к] ?? к}
                    {!ИМЯ_СЧЁТЧИКА[к] && (
                      <div className="muted mono">{к}</div>
                    )}
                  </td>
                  <td className="num" data-label="Сейчас">{свежий.data[к]}</td>
                  <td className="num" data-label="Изменение">
                    {свежий.delta === null || д === undefined ? (
                      <span className="muted" title="сравнивать не с чем">—</span>
                    ) : д === 0 ? (
                      <span className="muted">без изменений</span>
                    ) : (
                      <span className={д > 0 ? 'error' : ''}>{д > 0 ? `+${д}` : д}</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Происхождение выписки. Единственный способ увидеть дыру в периоде: месяц без операций
 * в журнале заливок никак не отражается.
 */
function ЖурналЗаливок({ данные }: { данные?: NonNullable<ReturnType<typeof useЗаливки>['data']> }) {
  if (!данные) return null
  return (
    <div className="card">
      <b>Откуда взялась выписка</b>
      <div className="hint" style={{ marginTop: 4 }}>
        {данные.data_notes?.manual_upload}
      </div>
      <div className="table-wrap cards" style={{ marginTop: 8 }}>
        <table>
          <thead>
            <tr>
              <th>Файл</th>
              <th>Период</th>
              <th className="num">Строк в файле</th>
              <th className="num">Добавлено</th>
              <th className="num">Дублей</th>
              <th>Кто и когда</th>
            </tr>
          </thead>
          <tbody>
            {данные.rows.map((з) => (
              <tr key={з.src_id}>
                <td data-label="Файл">
                  <div className="mono" style={{ fontSize: 12 }}>{з.fname || '—'}</div>
                  <div className="hint">{з.bank || 'банк не указан'}</div>
                </td>
                <td data-label="Период" className="nowrap">{з.period || '—'}</td>
                <td className="num" data-label="Строк в файле">{з.total_rows ?? '—'}</td>
                <td className="num" data-label="Добавлено">{з.added ?? '—'}</td>
                <td className="num" data-label="Дублей">{з.dup ?? '—'}</td>
                <td data-label="Кто и когда">
                  <div>{з.by_user || '—'}</div>
                  <div className="hint">{fmtDate(з.created_at, true)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12 }}><b>Что реально покрыто по месяцам</b></div>
      <div className="table-wrap cards" style={{ marginTop: 8 }}>
        <table>
          <thead>
            <tr>
              <th>Месяц</th>
              <th className="num">Операций</th>
              <th>Первый и последний день</th>
              <th className="num">Приход</th>
              <th className="num">Расход</th>
            </tr>
          </thead>
          <tbody>
            {данные.coverage.map((м) => (
              <tr key={м.month}>
                <td data-label="Месяц" className="nowrap">{месяцСловами(м.month)}</td>
                <td className="num" data-label="Операций">{м.n}</td>
                <td data-label="Первый и последний день" className="nowrap">
                  {fmtDate(м.first_day)} — {fmtDate(м.last_day)}
                </td>
                <td className="num" data-label="Приход">{fmtMoney(м.in_rub ?? 0)}</td>
                <td className="num" data-label="Расход">{fmtMoney(м.out_rub ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
